/**
 * OverlayControls — the WMS stencil tool for a given MapState: pick a curated
 * overlay (only ones whose coverage contains the site), tweak the active layer,
 * reuse a saved one, or add your own by file / URL. Shared by the brief map and
 * the design map view. Mirrors the real-app StencilButton, Studio styled.
 */
import { useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import type { MapState } from "src/types/MapState";
import type { World } from "src/types/World";
import { WMS_CATALOG } from "src/constants/wmsCatalog";
import {
  pointInBounds,
  type CuratedWmsEntry,
  type SavedWmsEntry,
} from "src/types/stencil/wmsCatalog";
import { Icon, ACCENT } from "./Icon";

const mapIcon = (
  <g>
    <path d="M4 5h16v14H4z" />
    <path d="M4 15l5-5 4 4 3-3 4 4" />
  </g>
);
const icons = {
  x: <path d="M6 6l12 12M18 6L6 18" />,
  check: <path d="M5 13l4 4 10-10" />,
  upload: <path d="M12 16V5M7 10l5-5 5 5M5 19h14" />,
  link: <path d="M9 15l6-6M10 7l1-1a3.5 3.5 0 0 1 5 5l-1 1M14 17l-1 1a3.5 3.5 0 0 1-5-5l1-1" />,
};

const sectionTitle = "text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af] mb-1.5";

export const OverlayControls = observer(function OverlayControls({
  mapState,
  world,
}: {
  mapState: MapState;
  world: World | null;
}) {
  const stencil = mapState.stencil;
  const point = mapState.viewportCenter;
  const fileRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<"file" | "url">("url");
  const [urlInput, setUrlInput] = useState("");

  // Only curated overlays whose coverage actually contains the site.
  const areaCatalog = useMemo(
    () => (point ? WMS_CATALOG.filter((e) => pointInBounds(point, e.bounds)) : []),
    [point]
  );
  const activeId = stencil.loadedCatalogId;
  const saved = world?.stencilCatalog ?? [];
  const wmsLayers = stencil.mode.kind === "wms" ? stencil.mode.capabilities.layers : [];
  const selectedLayerName = stencil.mode.kind === "wms" ? stencil.mode.selectedLayerName : "";

  const loadCurated = (e: CuratedWmsEntry) =>
    e.id === activeId ? stencil.clear() : void stencil.loadCatalogEntry(e);
  const loadSaved = (e: SavedWmsEntry) => void stencil.loadCatalogEntry(e);
  const onLoadUrl = () => {
    if (!world || !urlInput.trim()) return;
    void stencil.setUrl(urlInput, world).then(() => {
      if (!stencil.error) setUrlInput("");
    });
  };

  return (
    <div className="space-y-3">
      <section>
        <h5 className={sectionTitle}>Catalog · this site</h5>
        {areaCatalog.length > 0 ? (
          <div className="space-y-1 max-h-44 overflow-y-auto">
            {areaCatalog.map((e) => {
              const on = e.id === activeId;
              return (
                <button
                  key={e.id}
                  onClick={() => loadCurated(e)}
                  className={
                    "w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[11px] " +
                    (on ? "text-[#7c5cff]" : "text-[#4b5563] hover:bg-[#f3f4f6]")
                  }
                  style={on ? { background: "rgba(124,92,255,0.1)" } : undefined}
                >
                  <span className="w-5 h-5 shrink-0 grid place-items-center rounded-[3px] border border-[#d1d5db] bg-[#f3f4f6] text-[#9ca3af]">
                    <Icon d={mapIcon} size={12} />
                  </span>
                  <span className="min-w-0 grow">
                    <span className="block truncate">{e.title}</span>
                    <span className="block text-[9px] text-[#9ca3af] uppercase tracking-wide">
                      {e.state} · {e.category}
                    </span>
                  </span>
                  {on && <Icon d={icons.check} size={12} />}
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-[11px] text-[#9ca3af] px-0.5 py-1">
            No catalog overlays cover this site — add your own below.
          </p>
        )}
      </section>

      {stencil.hasContent && (
        <section className="border-t border-[#eef0f2] pt-2.5 space-y-2">
          <h5 className={sectionTitle}>Active overlay</h5>
          {stencil.isWms && wmsLayers.length > 1 && (
            <select
              value={selectedLayerName}
              onChange={(e) => stencil.selectWmsLayer(e.target.value)}
              className="w-full text-[11px] px-2 py-1.5 rounded-md border border-[#e5e7eb] bg-white text-[#4b5563]"
            >
              {wmsLayers.map((l) => (
                <option key={l.name} value={l.name}>
                  {l.title}
                </option>
              ))}
            </select>
          )}
          {stencil.isLocked && !stencil.editMode && (
            <button
              onClick={() => stencil.enterEditMode()}
              className="w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-md border border-[#e5e7eb] text-[11px] font-medium text-[#4b5563] hover:bg-[#f9fafb]"
            >
              Adjust position
            </button>
          )}
          <div className="flex items-center gap-2 px-0.5">
            <span className="text-[10px] text-[#9ca3af] w-12 shrink-0">Opacity</span>
            <input
              type="range"
              min={0}
              max={100}
              value={stencil.opacity.value}
              onChange={(e) => stencil.opacity.set(+e.target.value)}
              className="grow accent-[#7c5cff]"
            />
            <span className="text-[10px] text-[#6b7280] tabular-nums w-8 text-right">
              {Math.round(stencil.opacity.value)}%
            </span>
          </div>
          <button
            onClick={() => stencil.clear()}
            className="w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-md border border-[#fca5a5] text-[11px] font-medium text-[#dc2626] hover:bg-[#fef2f2]"
          >
            <Icon d={icons.x} size={13} /> Remove overlay
          </button>
        </section>
      )}

      {saved.length > 0 && (
        <section className="border-t border-[#eef0f2] pt-2.5">
          <h5 className={sectionTitle}>Saved</h5>
          <div className="space-y-1">
            {saved.map((e) => (
              <div key={e.url} className="flex items-center gap-1">
                <button
                  onClick={() => loadSaved(e)}
                  className="grow text-left text-[11px] text-[#4b5563] px-2 py-1.5 rounded-md hover:bg-[#f3f4f6] truncate"
                >
                  {e.title}
                </button>
                <button
                  onClick={() => world?.removeFromStencilCatalog(e.url)}
                  title="Remove saved overlay"
                  className="shrink-0 w-6 h-6 grid place-items-center rounded-sm text-[#d1d5db] hover:text-[#4b5563]"
                >
                  <Icon d={icons.x} size={13} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="border-t border-[#eef0f2] pt-2.5 space-y-1.5">
        <h5 className={sectionTitle}>Add your own</h5>
        <div className="flex rounded-md border border-[#e5e7eb] overflow-hidden text-[11px]">
          {(["url", "file"] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={
                "flex-1 py-1.5 font-medium " +
                (tab === t ? "text-white" : "text-[#6b7280] hover:bg-[#f9fafb]")
              }
              style={tab === t ? { background: ACCENT } : undefined}
            >
              {t === "url" ? "URL" : "File"}
            </button>
          ))}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,.xml,.wms,application/xml,text/xml"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void stencil.setFile(file, world);
            e.target.value = "";
          }}
        />
        {tab === "file" ? (
          <button
            onClick={() => fileRef.current?.click()}
            className="w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-md border border-[#e5e7eb] text-[11px] font-medium text-[#4b5563] hover:bg-[#f9fafb]"
          >
            <Icon d={icons.upload} size={13} /> Upload image / WMS
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <input
              type="url"
              placeholder="https://…/wms"
              value={urlInput}
              disabled={!world}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onLoadUrl()}
              className="grow min-w-0 text-[11px] px-2 py-1.5 rounded-md border border-[#e5e7eb] bg-white text-[#4b5563] disabled:bg-[#f9fafb]"
            />
            <button
              onClick={onLoadUrl}
              disabled={!world || !urlInput.trim()}
              className="shrink-0 h-8 px-2.5 inline-flex items-center gap-1 rounded-md text-white text-[11px] font-medium disabled:opacity-40"
              style={{ background: ACCENT }}
            >
              <Icon d={icons.link} size={13} /> Load
            </button>
          </div>
        )}
        {stencil.error && (
          <p className="text-[10px] text-[#dc2626] px-0.5">
            Couldn&apos;t load that overlay — check the file or URL.
          </p>
        )}
      </section>
    </div>
  );
});
